import { KeyboardEvent, RefObject, useLayoutEffect } from 'react';

/** Props shared by login username/password fields for mobile keyboard "Go" (Android + iOS Safari 13.4+). */
export const LOGIN_GO_KEYBOARD_PROPS = {
  enterKeyHint: 'go' as const,
};

/** Focus an input after paint — retries until the field receives focus. */
export function focusInputRef(
  ref: RefObject<HTMLInputElement | null>,
  delayMs = 0
): () => void {
  let cancelled = false;
  const timers: number[] = [];

  const tryFocus = (): boolean => {
    if (cancelled) {
      return false;
    }
    const el = ref.current;
    if (!el || el.disabled) {
      return false;
    }
    try {
      el.focus({ preventScroll: false });
    } catch {
      el.focus();
    }
    return document.activeElement === el;
  };

  const schedule = (ms: number) => {
    timers.push(
      window.setTimeout(() => {
        tryFocus();
      }, ms)
    );
  };

  schedule(delayMs);
  [50, 150, 350].forEach((ms) => schedule(delayMs + ms));

  return () => {
    cancelled = true;
    timers.forEach((id) => window.clearTimeout(id));
  };
}

/** Keep the active login field focused when the step changes or the field mounts. */
export function useLoginFieldFocus(
  ref: RefObject<HTMLInputElement | null>,
  active: boolean
): void {
  useLayoutEffect(() => {
    if (!active) {
      return;
    }
    return focusInputRef(ref);
  }, [ref, active]);
}

/**
 * Enter / mobile keyboard Go — ensures form submit on iOS where implicit submit can be unreliable.
 */
export function handleEnterOrGoKeyDown(
  e: KeyboardEvent<HTMLInputElement>,
  formRef: RefObject<HTMLFormElement | null>
) {
  if (e.key !== 'Enter' || e.nativeEvent.isComposing) {
    return;
  }
  const form = formRef.current ?? e.currentTarget.form;
  if (!form) {
    return;
  }
  e.preventDefault();
  if (typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  } else {
    form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }
}
